import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import "./index.css";
import {
  connectSync,
  disconnectSync,
  sendState,
  onRemoteState,
  onBoardsUpdated,
  setWebSocketOpenHandler,
  type PersistedState,
  type ServerMessage,
} from "./storage";
import { authClient } from "./lib/auth-client";
import { notifyBoardListChanged, subscribeBoardListChanged } from "./lib/board-sync";
import { AuthPage } from "./AuthPage";

const uuid = () =>
  typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
        b.toString(16).padStart(2, "0")
      )
        .join("")
        .replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, "$1-$2-$3-$4-$5");

interface CanvasItemBase {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
}

interface CanvasItemImage extends CanvasItemBase {
  type: "image";
  src: string;
  naturalWidth: number;
  naturalHeight: number;
}

interface CanvasItemText extends CanvasItemBase {
  type: "text";
  text: string;
  fontSize: number;
  lineHeight: number;
}

type CanvasItem = CanvasItemImage | CanvasItemText;

interface ContextMenu {
  x: number;
  y: number;
  itemId: string | null;
}

interface CanvasSummary {
  id: string;
  name: string;
  updatedAt: string;
}

type ActiveTool = "pan" | "text";

const LAST_CANVAS_KEY = "canvas:last-board-id";
const canvasListRequest: RequestInit = { cache: "no-store" };
const BOARD_MENU_WIDTH_PX = 132;
const DEMO_BOARDS_KEY = "demo:boards";
const DEMO_CANVAS_PREFIX = "demo:canvas:";
const DEFAULT_TEXT = "Text";
const DEFAULT_TEXT_WIDTH = 220;
const DEFAULT_TEXT_HEIGHT = 120;
const DEFAULT_TEXT_FONT_SIZE = 24;
const DEFAULT_TEXT_LINE_HEIGHT = 1.3;

function isImageItem(item: CanvasItem): item is CanvasItemImage {
  return item.type === "image";
}

function isTextItem(item: CanvasItem): item is CanvasItemText {
  return item.type === "text";
}

function withZOrder<T extends CanvasItem>(items: T[]): T[] {
  return items.map((item, index) => ({ ...item, zIndex: index })) as T[];
}

function persistedToItems(state: PersistedState | null): CanvasItem[] {
  if (!state) return [];
  const images: CanvasItemImage[] = state.images.map((img) => ({
    id: img.id,
    type: "image",
    src: img.url,
    x: img.x,
    y: img.y,
    width: img.width,
    height: img.height,
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight,
    zIndex: img.zIndex,
  }));
  const texts: CanvasItemText[] = (state.texts ?? []).map((txt) => ({
    id: txt.id,
    type: "text",
    text: txt.text,
    x: txt.x,
    y: txt.y,
    width: txt.width,
    height: txt.height,
    fontSize: txt.fontSize,
    lineHeight: txt.lineHeight,
    zIndex: txt.zIndex,
  }));
  return withZOrder([...images, ...texts].sort((a, b) => a.zIndex - b.zIndex));
}

function itemsToPersistedState(
  items: CanvasItem[],
  camera: { x: number; y: number; zoom: number }
): PersistedState {
  const ordered = withZOrder(items);
  return {
    camera,
    images: ordered.filter(isImageItem).map((item) => ({
      id: item.id,
      url: item.src,
      x: item.x,
      y: item.y,
      width: item.width,
      height: item.height,
      naturalWidth: item.naturalWidth,
      naturalHeight: item.naturalHeight,
      zIndex: item.zIndex,
    })),
    texts: ordered.filter(isTextItem).map((item) => ({
      id: item.id,
      text: item.text,
      x: item.x,
      y: item.y,
      width: item.width,
      height: item.height,
      fontSize: item.fontSize,
      lineHeight: item.lineHeight,
      zIndex: item.zIndex,
    })),
  };
}

function getDemoBoards(): CanvasSummary[] {
  try {
    return JSON.parse(localStorage.getItem(DEMO_BOARDS_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveDemoBoards(boards: CanvasSummary[]) {
  localStorage.setItem(DEMO_BOARDS_KEY, JSON.stringify(boards));
}

function getDemoCanvas(id: string): PersistedState | null {
  try {
    const raw = JSON.parse(localStorage.getItem(DEMO_CANVAS_PREFIX + id) || "null");
    if (!raw) return null;
    return {
      camera: raw.camera ?? { x: 0, y: 0, zoom: 1 },
      images: raw.images ?? [],
      texts: raw.texts ?? [],
    };
  } catch {
    return null;
  }
}

function saveDemoCanvas(
  id: string,
  camera: { x: number; y: number; zoom: number },
  items: CanvasItem[]
) {
  localStorage.setItem(
    DEMO_CANVAS_PREFIX + id,
    JSON.stringify(itemsToPersistedState(items, camera))
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return true;
  return target.isContentEditable;
}

function preserveEditingItem(remoteItems: CanvasItem[], localItems: CanvasItem[], editingId: string | null) {
  if (!editingId) return remoteItems;
  const localItem = localItems.find((item) => item.id === editingId && isTextItem(item));
  if (!localItem) return remoteItems;
  const next = remoteItems.some((item) => item.id === editingId)
    ? remoteItems.map((item) => (item.id === editingId ? localItem : item))
    : [...remoteItems, localItem];
  return withZOrder(next.sort((a, b) => a.zIndex - b.zIndex));
}

export function App() {
  const session = authClient.useSession();
  const [demoMode, setDemoMode] = useState(false);

  if (demoMode) {
    return (
      <Canvas
        isDemo
        onExitDemo={() => setDemoMode(false)}
        user={{ name: "Demo User", email: "" }}
        onSignOut={() => setDemoMode(false)}
      />
    );
  }

  if (session.isPending) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!session.data?.user) {
    return <AuthPage onAuth={() => session.refetch()} onDemo={() => setDemoMode(true)} />;
  }

  return <Canvas user={session.data.user} onSignOut={() => authClient.signOut()} />;
}

function Canvas({
  user,
  onSignOut,
  isDemo,
  onExitDemo,
}: {
  user: { name: string; email: string };
  onSignOut: () => void;
  isDemo?: boolean;
  onExitDemo?: () => void;
}) {
  const [items, setItems] = useState<CanvasItem[]>([]);
  const [camera, setCamera] = useState({ x: 0, y: 0, zoom: 1 });
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<ActiveTool>("pan");
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState("");
  const [editingOriginal, setEditingOriginal] = useState("");
  const [editingIsNew, setEditingIsNew] = useState(false);
  const [dragging, setDragging] = useState<{
    id: string;
    startX: number;
    startY: number;
    startItemX: number;
    startItemY: number;
  } | null>(null);
  const [panning, setPanning] = useState<{
    startX: number;
    startY: number;
    startCamX: number;
    startCamY: number;
  } | null>(null);
  const [resizing, setResizing] = useState<{
    id: string;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    aspectRatio?: number;
    minWidth: number;
    minHeight: number;
  } | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const [showHint, setShowHint] = useState(true);
  const [canvasList, setCanvasList] = useState<CanvasSummary[] | null>(null);
  const [activeCanvasId, setActiveCanvasId] = useState<string | null>(null);
  const [workspaceReady, setWorkspaceReady] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [boardMenuId, setBoardMenuId] = useState<string | null>(null);
  const [boardMenuPos, setBoardMenuPos] = useState({ top: 0, left: 0 });
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cameraRef = useRef(camera);
  const itemsRef = useRef(items);
  const draggingRef = useRef(dragging);
  const resizingRef = useRef(resizing);
  const panningRef = useRef(panning);
  const editingTextIdRef = useRef(editingTextId);
  const isRemoteUpdate = useRef(false);
  const persistRef = useRef({ items, camera, loaded });

  cameraRef.current = camera;
  itemsRef.current = items;
  draggingRef.current = dragging;
  resizingRef.current = resizing;
  panningRef.current = panning;
  editingTextIdRef.current = editingTextId;
  persistRef.current = { items, camera, loaded };

  useEffect(() => {
    if (!editingTextId) return;
    textareaRef.current?.focus();
    textareaRef.current?.select();
  }, [editingTextId]);

  useEffect(() => {
    if (!boardMenuId) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest(".canvas-sidebar-popover") || t.closest(".canvas-sidebar-kebab")) return;
      setBoardMenuId(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setBoardMenuId(null);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [boardMenuId]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest(".user-menu")) return;
      setUserMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setUserMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [userMenuOpen]);

  const refreshCanvasList = useCallback(async (): Promise<CanvasSummary[] | null> => {
    if (isDemo) {
      const list = getDemoBoards();
      setCanvasList(list);
      return list;
    }
    const r = await fetch("/api/canvases", canvasListRequest);
    if (!r.ok) return null;
    const list = (await r.json()) as CanvasSummary[];
    setCanvasList(list);
    return list;
  }, [isDemo]);

  const reconcileActiveBoard = useCallback((list: CanvasSummary[]) => {
    setActiveCanvasId((current) => {
      if (current && list.some((c) => c.id === current)) return current;
      const next = list[0]?.id ?? null;
      if (next) localStorage.setItem(LAST_CANVAS_KEY, next);
      return next;
    });
  }, []);

  const refreshCanvasListRef = useRef(refreshCanvasList);
  refreshCanvasListRef.current = refreshCanvasList;
  const reconcileActiveBoardRef = useRef(reconcileActiveBoard);
  reconcileActiveBoardRef.current = reconcileActiveBoard;

  useEffect(() => {
    if (isDemo) return;
    setWebSocketOpenHandler(() => {
      void refreshCanvasListRef.current().then((list) => {
        if (list) reconcileActiveBoardRef.current(list);
      });
      const { items: localItems, camera: localCamera, loaded: ready } = persistRef.current;
      if (!ready) return;
      sendState(itemsToPersistedState(localItems, localCamera));
    });
    return () => setWebSocketOpenHandler(null);
  }, [isDemo]);

  useEffect(() => {
    if (isDemo) return;
    onBoardsUpdated(() => {
      void refreshCanvasList().then((list) => {
        if (list) reconcileActiveBoard(list);
      });
    });
    return () => onBoardsUpdated(null);
  }, [isDemo, refreshCanvasList, reconcileActiveBoard]);

  useEffect(() => {
    if (isDemo) return;
    return subscribeBoardListChanged(() => {
      void refreshCanvasList().then((list) => {
        if (list) reconcileActiveBoard(list);
      });
    });
  }, [isDemo, refreshCanvasList, reconcileActiveBoard]);

  const selectCanvas = useCallback(
    (id: string) => {
      if (id === activeCanvasId) return;
      localStorage.setItem(LAST_CANVAS_KEY, id);
      setActiveCanvasId(id);
      setBoardMenuId(null);
      setRenamingId(null);
    },
    [activeCanvasId]
  );

  const createCanvas = useCallback(async () => {
    const n = (canvasList?.length ?? 0) + 1;
    if (isDemo) {
      const id = uuid();
      const boards = getDemoBoards();
      const row: CanvasSummary = { id, name: `Board ${n}`, updatedAt: new Date().toISOString() };
      boards.unshift(row);
      saveDemoBoards(boards);
      saveDemoCanvas(id, { x: 0, y: 0, zoom: 1 }, []);
      setCanvasList(boards);
      localStorage.setItem(LAST_CANVAS_KEY, id);
      setActiveCanvasId(id);
      setBoardMenuId(null);
      return;
    }
    const res = await fetch("/api/canvases", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: `Board ${n}` }),
    });
    if (!res.ok) return;
    const row = (await res.json()) as CanvasSummary;
    void refreshCanvasList();
    notifyBoardListChanged();
    localStorage.setItem(LAST_CANVAS_KEY, row.id);
    setActiveCanvasId(row.id);
    setBoardMenuId(null);
  }, [isDemo, canvasList?.length, refreshCanvasList]);

  const deleteCanvas = useCallback(
    async (id: string) => {
      if (!canvasList || canvasList.length <= 1) return;
      if (!confirm("Delete this board and all content on it?")) return;
      if (isDemo) {
        const boards = getDemoBoards().filter((b) => b.id !== id);
        saveDemoBoards(boards);
        localStorage.removeItem(DEMO_CANVAS_PREFIX + id);
        setCanvasList(boards);
        setBoardMenuId(null);
        if (id === activeCanvasId) {
          const next = boards[0]?.id;
          if (next) {
            localStorage.setItem(LAST_CANVAS_KEY, next);
            setActiveCanvasId(next);
          }
        }
        return;
      }
      const res = await fetch(`/api/canvases/${encodeURIComponent(id)}`, { method: "DELETE" });
      if (!res.ok) return;
      const list = await refreshCanvasList();
      if (!list) return;
      notifyBoardListChanged();
      setBoardMenuId(null);
      if (id === activeCanvasId) {
        const next = list[0]?.id;
        if (next) {
          localStorage.setItem(LAST_CANVAS_KEY, next);
          setActiveCanvasId(next);
        }
      }
    },
    [isDemo, canvasList, activeCanvasId, refreshCanvasList]
  );

  const commitRename = useCallback(async () => {
    const id = renamingId;
    const name = renameDraft.trim();
    setRenamingId(null);
    if (!id || !name) return;
    if (isDemo) {
      const boards = getDemoBoards().map((b) => (b.id === id ? { ...b, name } : b));
      saveDemoBoards(boards);
      setCanvasList(boards);
      return;
    }
    const res = await fetch(`/api/canvases/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    if (res.ok) {
      await refreshCanvasList();
      notifyBoardListChanged();
    }
  }, [isDemo, renamingId, renameDraft, refreshCanvasList]);

  useEffect(() => {
    let cancelled = false;
    if (isDemo) {
      let boards = getDemoBoards();
      if (boards.length === 0) {
        const id = uuid();
        boards = [{ id, name: "Board 1", updatedAt: new Date().toISOString() }];
        saveDemoBoards(boards);
        saveDemoCanvas(id, { x: 0, y: 0, zoom: 1 }, []);
      }
      setCanvasList(boards);
      const stored = localStorage.getItem(LAST_CANVAS_KEY);
      const next = stored && boards.some((c) => c.id === stored) ? stored : boards[0]?.id ?? null;
      setActiveCanvasId(next);
      setWorkspaceReady(true);
      return;
    }
    fetch("/api/canvases", canvasListRequest)
      .then((r) => r.json())
      .then((list: CanvasSummary[]) => {
        if (cancelled) return;
        setCanvasList(list);
        const stored = localStorage.getItem(LAST_CANVAS_KEY);
        const next = stored && list.some((c) => c.id === stored) ? stored : list[0]?.id ?? null;
        setActiveCanvasId(next);
        setWorkspaceReady(true);
      })
      .catch(() => {
        if (!cancelled) setWorkspaceReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isDemo]);

  useEffect(() => {
    if (!activeCanvasId) return;
    let cancelled = false;
    setLoaded(false);
    setSelectedId(null);
    setEditingTextId(null);
    setEditingDraft("");
    setEditingOriginal("");
    setEditingIsNew(false);
    setActiveTool("pan");

    if (isDemo) {
      const saved = getDemoCanvas(activeCanvasId);
      if (saved) {
        setItems(persistedToItems(saved));
        setCamera(saved.camera);
      } else {
        setItems([]);
        setCamera({ x: 0, y: 0, zoom: 1 });
      }
      setLoaded(true);
      return;
    }

    disconnectSync();
    connectSync(activeCanvasId).then((state) => {
      if (cancelled) return;
      if (state) {
        setItems(persistedToItems(state));
        setCamera(state.camera);
      } else {
        setItems([]);
        setCamera({ x: 0, y: 0, zoom: 1 });
      }
      setLoaded(true);
    });
    return () => {
      cancelled = true;
      disconnectSync();
    };
  }, [isDemo, activeCanvasId]);

  useEffect(() => {
    if (!loaded || isDemo) return;
    onRemoteState((msg: ServerMessage) => {
      const interacting = draggingRef.current || resizingRef.current || panningRef.current;
      if (msg.type === "state:meta" && interacting) return;

      isRemoteUpdate.current = true;
      const localItems = itemsRef.current;
      const currentEditingId = editingTextIdRef.current;

      if (msg.type === "state:full") {
        const remoteItems = persistedToItems(msg.state);
        setItems(preserveEditingItem(remoteItems, localItems, currentEditingId));
        setCamera(msg.state.camera);
        return;
      }

      if (msg.type !== "state:meta") return;

      const imageMetaById = new Map(
        msg.state.images.map((item): [string, (typeof msg.state.images)[number]] => [item.id, item])
      );
      const textMetaById = new Map(
        msg.state.texts.map((item): [string, (typeof msg.state.texts)[number]] => [item.id, item])
      );
      const merged = localItems
        .filter((item) =>
          isImageItem(item) ? imageMetaById.has(item.id) : textMetaById.has(item.id)
        )
        .map((item) => {
          if (currentEditingId && item.id === currentEditingId && isTextItem(item)) {
            return item;
          }
          if (isImageItem(item)) {
            const meta = imageMetaById.get(item.id)!;
            return {
              ...item,
              x: meta.x,
              y: meta.y,
              width: meta.width,
              height: meta.height,
              naturalWidth: meta.naturalWidth,
              naturalHeight: meta.naturalHeight,
              zIndex: meta.zIndex,
            };
          }
          const meta = textMetaById.get(item.id)!;
          return {
            ...item,
            x: meta.x,
            y: meta.y,
            width: meta.width,
            height: meta.height,
            fontSize: meta.fontSize,
            lineHeight: meta.lineHeight,
            zIndex: meta.zIndex,
          };
        });
      setItems(withZOrder(merged.sort((a, b) => a.zIndex - b.zIndex)));
      setCamera(msg.state.camera);
    });
  }, [loaded, isDemo]);

  const saveTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    if (!loaded) return;
    clearTimeout(saveTimeout.current);
    if (isRemoteUpdate.current) {
      isRemoteUpdate.current = false;
      return;
    }
    saveTimeout.current = setTimeout(() => {
      if (isDemo) {
        if (activeCanvasId) saveDemoCanvas(activeCanvasId, camera, items);
        return;
      }
      sendState(itemsToPersistedState(items, camera));
    }, 500);
  }, [isDemo, activeCanvasId, items, camera, loaded]);

  useEffect(() => {
    if (items.length > 0) setShowHint(false);
  }, [items.length]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      setContextMenu(null);
      const sidebar = (e.target as HTMLElement).closest?.(".canvas-sidebar");
      if (!sidebar) setBoardMenuId(null);
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  useEffect(() => {
    const prevent = (e: Event) => e.preventDefault();
    document.addEventListener("gesturestart", prevent, { passive: false });
    document.addEventListener("gesturechange", prevent, { passive: false });
    return () => {
      document.removeEventListener("gesturestart", prevent);
      document.removeEventListener("gesturechange", prevent);
    };
  }, []);

  const screenToCanvas = useCallback(
    (sx: number, sy: number) => ({
      x: (sx - camera.x) / camera.zoom,
      y: (sy - camera.y) / camera.zoom,
    }),
    [camera]
  );

  const startEditingText = useCallback((id: string, isNew: boolean) => {
    const item = itemsRef.current.find((entry) => entry.id === id);
    if (!item || !isTextItem(item)) return;
    setSelectedId(id);
    setEditingTextId(id);
    setEditingDraft(item.text);
    setEditingOriginal(item.text);
    setEditingIsNew(isNew);
    setActiveTool("pan");
  }, []);

  const commitTextEdit = useCallback(() => {
    const id = editingTextIdRef.current;
    if (!id) return;
    const draft = editingDraft;
    const trimmed = draft.trim();
    setEditingTextId(null);
    setEditingDraft("");
    setActiveTool("pan");
    if (!trimmed) {
      if (editingIsNew) {
        setItems((prev) => withZOrder(prev.filter((item) => item.id !== id)));
        setSelectedId(null);
      } else {
        setItems((prev) =>
          prev.map((item) =>
            item.id === id && isTextItem(item) ? { ...item, text: editingOriginal } : item
          )
        );
      }
      setEditingOriginal("");
      setEditingIsNew(false);
      return;
    }
    setItems((prev) =>
      prev.map((item) => (item.id === id && isTextItem(item) ? { ...item, text: draft } : item))
    );
    setEditingOriginal("");
    setEditingIsNew(false);
  }, [editingDraft, editingIsNew, editingOriginal]);

  const cancelTextEdit = useCallback(() => {
    const id = editingTextIdRef.current;
    if (!id) return;
    setEditingTextId(null);
    setEditingDraft("");
    setActiveTool("pan");
    if (editingIsNew) {
      setItems((prev) => withZOrder(prev.filter((item) => item.id !== id)));
      setSelectedId(null);
    } else {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id && isTextItem(item) ? { ...item, text: editingOriginal } : item
        )
      );
    }
    setEditingOriginal("");
    setEditingIsNew(false);
  }, [editingIsNew, editingOriginal]);

  const addTextAtScreenPoint = useCallback(
    (clientX: number, clientY: number) => {
      const { x, y } = screenToCanvas(clientX, clientY);
      const id = uuid();
      const next: CanvasItemText = {
        id,
        type: "text",
        text: DEFAULT_TEXT,
        x: x - DEFAULT_TEXT_WIDTH / 2,
        y: y - DEFAULT_TEXT_HEIGHT / 2,
        width: DEFAULT_TEXT_WIDTH,
        height: DEFAULT_TEXT_HEIGHT,
        fontSize: DEFAULT_TEXT_FONT_SIZE,
        lineHeight: DEFAULT_TEXT_LINE_HEIGHT,
        zIndex: itemsRef.current.length,
      };
      setItems((prev) => withZOrder([...prev, next]));
      setSelectedId(id);
      setEditingTextId(id);
      setEditingDraft(DEFAULT_TEXT);
      setEditingOriginal(DEFAULT_TEXT);
      setEditingIsNew(true);
      setActiveTool("pan");
    },
    [screenToCanvas]
  );

  const addImage = useCallback(
    async (file: Blob, naturalWidth: number, naturalHeight: number, cx?: number, cy?: number) => {
      if (!activeCanvasId) return;
      const maxDim = 500;
      const scale = Math.min(maxDim / naturalWidth, maxDim / naturalHeight, 1);
      const width = naturalWidth * scale;
      const height = naturalHeight * scale;
      const container = containerRef.current;
      const centerX = cx ?? (container ? container.clientWidth / 2 : 500);
      const centerY = cy ?? (container ? container.clientHeight / 2 : 400);
      const canvasPos = screenToCanvas(centerX, centerY);
      const x = canvasPos.x - width / 2;
      const y = canvasPos.y - height / 2;

      if (isDemo) {
        const reader = new FileReader();
        reader.onload = () => {
          const img: CanvasItemImage = {
            id: uuid(),
            type: "image",
            src: reader.result as string,
            x,
            y,
            width,
            height,
            naturalWidth,
            naturalHeight,
            zIndex: itemsRef.current.length,
          };
          setItems((prev) => withZOrder([...prev, img]));
          setSelectedId(img.id);
        };
        reader.readAsDataURL(file);
        return;
      }

      const form = new FormData();
      form.append("file", file);
      form.append("canvasId", activeCanvasId);
      form.append("x", String(x));
      form.append("y", String(y));
      form.append("width", String(width));
      form.append("height", String(height));
      form.append("naturalWidth", String(naturalWidth));
      form.append("naturalHeight", String(naturalHeight));

      const res = await fetch("/api/images", { method: "POST", body: form });
      if (!res.ok) return;
      const record = await res.json();

      const img: CanvasItemImage = {
        id: record.id,
        type: "image",
        src: record.url,
        x,
        y,
        width,
        height,
        naturalWidth,
        naturalHeight,
        zIndex: itemsRef.current.length,
      };
      setItems((prev) => withZOrder([...prev, img]));
      setSelectedId(img.id);
    },
    [isDemo, screenToCanvas, activeCanvasId]
  );

  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      if (editingTextIdRef.current) return;
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (!blob) continue;
          const url = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => addImage(blob, img.naturalWidth, img.naturalHeight);
          img.src = url;
          return;
        }
      }

      const text = e.clipboardData?.getData("text/plain");
      if (text && /\.(png|jpg|jpeg|gif|webp|svg|bmp)(\?.*)?$/i.test(text)) {
        e.preventDefault();
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = async () => {
          const res = await fetch(text);
          const blob = await res.blob();
          addImage(blob, img.naturalWidth, img.naturalHeight);
        };
        img.src = text;
      }
    };

    window.addEventListener("paste", handler);
    return () => window.removeEventListener("paste", handler);
  }, [addImage]);

  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.dataTransfer!.dropEffect = "copy";
    };

    const onDrop = (e: DragEvent) => {
      if (editingTextIdRef.current) return;
      e.preventDefault();
      const files = e.dataTransfer?.files;
      if (!files) return;

      for (const file of Array.from(files)) {
        if (file.type.startsWith("image/")) {
          const url = URL.createObjectURL(file);
          const img = new Image();
          img.onload = () => addImage(file, img.naturalWidth, img.naturalHeight, e.clientX, e.clientY);
          img.src = url;
        }
      }
    };

    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, [addImage]);

  const deleteItem = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((entry) => entry.id === id);
      if (!item) return;
      if (isImageItem(item) && !isDemo) {
        void fetch(`/api/images/${encodeURIComponent(id)}`, { method: "DELETE" });
      }
      if (editingTextIdRef.current === id) {
        setEditingTextId(null);
        setEditingDraft("");
        setEditingOriginal("");
        setEditingIsNew(false);
      }
      setItems((prev) => withZOrder(prev.filter((entry) => entry.id !== id)));
      setSelectedId((prev) => (prev === id ? null : prev));
    },
    [isDemo]
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest(".canvas-sidebar")) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "enter" && editingTextIdRef.current) {
        e.preventDefault();
        commitTextEdit();
        return;
      }

      if (e.key === "Escape") {
        if (editingTextIdRef.current) {
          e.preventDefault();
          cancelTextEdit();
          return;
        }
        setSelectedId(null);
        setContextMenu(null);
        setActiveTool("pan");
      }

      if ((e.key === "Delete" || e.key === "Backspace") && selectedId && !isTypingTarget(e.target)) {
        e.preventDefault();
        deleteItem(selectedId);
      }

      if ((e.metaKey || e.ctrlKey) && e.key === "0") {
        e.preventDefault();
        setCamera({ x: 0, y: 0, zoom: 1 });
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedId, cancelTextEdit, commitTextEdit, deleteItem]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (dragging) {
        const dx = (e.clientX - dragging.startX) / camera.zoom;
        const dy = (e.clientY - dragging.startY) / camera.zoom;
        setItems((prev) =>
          prev.map((item) =>
            item.id === dragging.id
              ? { ...item, x: dragging.startItemX + dx, y: dragging.startItemY + dy }
              : item
          )
        );
      } else if (panning) {
        setCamera((prev) => ({
          ...prev,
          x: panning.startCamX + (e.clientX - panning.startX),
          y: panning.startCamY + (e.clientY - panning.startY),
        }));
      } else if (resizing) {
        const dx = (e.clientX - resizing.startX) / camera.zoom;
        const dy = (e.clientY - resizing.startY) / camera.zoom;
        setItems((prev) =>
          prev.map((item) => {
            if (item.id !== resizing.id) return item;
            if (resizing.aspectRatio) {
              const nextWidth = Math.max(resizing.minWidth, resizing.startW + dx);
              const nextHeight = Math.max(resizing.minHeight, nextWidth / resizing.aspectRatio);
              return { ...item, width: nextWidth, height: nextHeight };
            }
            return {
              ...item,
              width: Math.max(resizing.minWidth, resizing.startW + dx),
              height: Math.max(resizing.minHeight, resizing.startH + dy),
            };
          })
        );
      }
    };

    const onMouseUp = () => {
      setDragging(null);
      setPanning(null);
      setResizing(null);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [dragging, panning, resizing, camera.zoom]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const isPinch = e.ctrlKey;
      const sensitivity = isPinch ? 0.01 : 0.001;
      const delta = -e.deltaY * sensitivity;
      const newZoom = Math.min(5, Math.max(0.1, cameraRef.current.zoom * (1 + delta)));
      const rect = container.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const cam = cameraRef.current;
      setCamera({
        zoom: newZoom,
        x: mx - (mx - cam.x) * (newZoom / cam.zoom),
        y: my - (my - cam.y) * (newZoom / cam.zoom),
      });
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, []);

  const onCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.button === 2) return;
    const target = e.target as HTMLElement;
    const hitBackground =
      e.target === containerRef.current || target.classList.contains("grid-pattern");
    if (!hitBackground) return;
    setContextMenu(null);
    setSelectedId(null);
    if (activeTool === "text") {
      addTextAtScreenPoint(e.clientX, e.clientY);
      return;
    }
    setPanning({
      startX: e.clientX,
      startY: e.clientY,
      startCamX: camera.x,
      startCamY: camera.y,
    });
  };

  const onItemMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (e.button === 2) return;
    if (editingTextId === id) return;
    const item = items.find((entry) => entry.id === id);
    if (!item) return;
    setSelectedId(id);
    setActiveTool("pan");
    setDragging({
      id,
      startX: e.clientX,
      startY: e.clientY,
      startItemX: item.x,
      startItemY: item.y,
    });
  };

  const onResizeMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const item = items.find((entry) => entry.id === id);
    if (!item) return;
    setResizing({
      id,
      startX: e.clientX,
      startY: e.clientY,
      startW: item.width,
      startH: item.height,
      aspectRatio: isImageItem(item) ? item.width / item.height : undefined,
      minWidth: isImageItem(item) ? 50 : 80,
      minHeight: isImageItem(item) ? 50 : 48,
    });
  };

  const onContextMenu = (e: React.MouseEvent, itemId: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    if (itemId) setSelectedId(itemId);
    setContextMenu({ x: e.clientX, y: e.clientY, itemId });
  };

  const bringToFront = useCallback((id: string) => {
    setItems((prev) => {
      const item = prev.find((entry) => entry.id === id);
      if (!item) return prev;
      return withZOrder([...prev.filter((entry) => entry.id !== id), item]);
    });
  }, []);

  const sendToBack = useCallback((id: string) => {
    setItems((prev) => {
      const item = prev.find((entry) => entry.id === id);
      if (!item) return prev;
      return withZOrder([item, ...prev.filter((entry) => entry.id !== id)]);
    });
  }, []);

  const duplicateItem = useCallback(
    async (id: string) => {
      if (!activeCanvasId) return;
      const item = itemsRef.current.find((entry) => entry.id === id);
      if (!item) return;

      if (isTextItem(item)) {
        const next: CanvasItemText = {
          ...item,
          id: uuid(),
          x: item.x + 20,
          y: item.y + 20,
          zIndex: itemsRef.current.length,
        };
        setItems((prev) => withZOrder([...prev, next]));
        setSelectedId(next.id);
        return;
      }

      if (isDemo) {
        const next: CanvasItemImage = {
          ...item,
          id: uuid(),
          x: item.x + 20,
          y: item.y + 20,
          zIndex: itemsRef.current.length,
        };
        setItems((prev) => withZOrder([...prev, next]));
        setSelectedId(next.id);
        return;
      }

      const form = new FormData();
      form.append("canvasId", activeCanvasId);
      form.append("sourceImageId", id);
      form.append("x", String(item.x + 20));
      form.append("y", String(item.y + 20));
      form.append("width", String(item.width));
      form.append("height", String(item.height));
      form.append("naturalWidth", String(item.naturalWidth));
      form.append("naturalHeight", String(item.naturalHeight));

      const res = await fetch("/api/images", { method: "POST", body: form });
      if (!res.ok) return;
      const record = await res.json();

      const next: CanvasItemImage = {
        id: record.id,
        type: "image",
        src: record.url,
        x: item.x + 20,
        y: item.y + 20,
        width: item.width,
        height: item.height,
        naturalWidth: item.naturalWidth,
        naturalHeight: item.naturalHeight,
        zIndex: itemsRef.current.length,
      };
      setItems((prev) => withZOrder([...prev, next]));
      setSelectedId(next.id);
    },
    [activeCanvasId, isDemo]
  );

  const fitToScreen = useCallback(() => {
    if (items.length === 0) {
      setCamera({ x: 0, y: 0, zoom: 1 });
      return;
    }
    const container = containerRef.current;
    if (!container) return;
    const padding = 80;
    const minX = Math.min(...items.map((item) => item.x));
    const minY = Math.min(...items.map((item) => item.y));
    const maxX = Math.max(...items.map((item) => item.x + item.width));
    const maxY = Math.max(...items.map((item) => item.y + item.height));
    const contentW = Math.max(maxX - minX, 1);
    const contentH = Math.max(maxY - minY, 1);
    const zoom = Math.min(
      (container.clientWidth - padding * 2) / contentW,
      (container.clientHeight - padding * 2) / contentH,
      2
    );
    setCamera({
      zoom,
      x: (container.clientWidth - contentW * zoom) / 2 - minX * zoom,
      y: (container.clientHeight - contentH * zoom) / 2 - minY * zoom,
    });
  }, [items]);

  const clearAll = useCallback(() => {
    if (!isDemo) {
      for (const item of itemsRef.current) {
        if (isImageItem(item)) {
          void fetch(`/api/images/${encodeURIComponent(item.id)}`, { method: "DELETE" });
        }
      }
    }
    setItems([]);
    setSelectedId(null);
    setEditingTextId(null);
    setEditingDraft("");
    setEditingOriginal("");
    setEditingIsNew(false);
  }, [isDemo]);

  const cursorClass = panning || dragging ? "grabbing" : activeTool === "text" ? "placing" : "";

  if (!workspaceReady || !activeCanvasId) {
    return (
      <div
        className="canvas-container"
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <span style={{ opacity: 0.4 }}>Loading…</span>
      </div>
    );
  }

  const gridSize = 30 * camera.zoom;
  const offsetX = camera.x % gridSize;
  const offsetY = camera.y % gridSize;
  const boardMenuCanvas =
    boardMenuId && canvasList ? canvasList.find((entry) => entry.id === boardMenuId) : undefined;

  return (
    <>
      <aside
        className="canvas-sidebar"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="canvas-sidebar-inner">
          <header className="canvas-sidebar-header">
            <span className="canvas-sidebar-title">Boards</span>
            <button type="button" className="canvas-sidebar-new" onClick={() => createCanvas()} title="New board">
              +
            </button>
          </header>
          <ul className="canvas-sidebar-list">
            {(canvasList ?? []).map((canvas) => (
              <li key={canvas.id} className={canvas.id === activeCanvasId ? "is-active" : ""}>
                {renamingId === canvas.id ? (
                  <input
                    className="canvas-sidebar-rename-input"
                    autoFocus
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void commitRename();
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                    onBlur={() => void commitRename()}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <>
                    <button
                      type="button"
                      className="canvas-sidebar-item"
                      onClick={() => selectCanvas(canvas.id)}
                    >
                      <span className="canvas-sidebar-item-name">{canvas.name}</span>
                    </button>
                    <div className="canvas-sidebar-item-actions">
                      <button
                        type="button"
                        className="canvas-sidebar-kebab"
                        aria-label="Board options"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (boardMenuId === canvas.id) {
                            setBoardMenuId(null);
                          } else {
                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setBoardMenuPos({
                              top: rect.bottom + 4,
                              left: rect.right - BOARD_MENU_WIDTH_PX,
                            });
                            setBoardMenuId(canvas.id);
                          }
                        }}
                      >
                        ⋯
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {boardMenuCanvas &&
        createPortal(
          <div
            className="canvas-sidebar-popover"
            style={{
              top: boardMenuPos.top,
              left: boardMenuPos.left,
              width: BOARD_MENU_WIDTH_PX,
              right: "auto",
            }}
            role="menu"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setRenameDraft(boardMenuCanvas.name);
                setRenamingId(boardMenuCanvas.id);
                setBoardMenuId(null);
              }}
            >
              Rename
            </button>
            <button
              type="button"
              className="danger"
              disabled={!canvasList || canvasList.length <= 1}
              onClick={(e) => {
                e.stopPropagation();
                void deleteCanvas(boardMenuCanvas.id);
              }}
            >
              Delete
            </button>
          </div>,
          document.body
        )}

      {isDemo && (
        <div className="demo-banner">
          Demo session — data is stored in your browser only and will be lost if you clear site data.
          <button type="button" onClick={onExitDemo}>
            Sign up for full access
          </button>
        </div>
      )}

      <div className="user-menu">
        <button
          type="button"
          className="user-menu-trigger"
          onClick={() => setUserMenuOpen((value) => !value)}
        >
          {user.name ?? user.email}
        </button>
        {userMenuOpen && (
          <div className="user-menu-dropdown">
            {isDemo ? (
              <>
                <span className="user-menu-email">Demo session</span>
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    onExitDemo?.();
                  }}
                >
                  Sign up for full access
                </button>
              </>
            ) : (
              <>
                <span className="user-menu-email">{user.email}</span>
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    onSignOut();
                  }}
                >
                  Sign Out
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        className={`canvas-container ${cursorClass}`}
        onMouseDown={loaded ? onCanvasMouseDown : undefined}
        onContextMenu={loaded ? (e) => onContextMenu(e, null) : undefined}
      >
        <svg className="grid-pattern" width="100%" height="100%">
          <defs>
            <pattern
              id="grid"
              width={gridSize}
              height={gridSize}
              patternUnits="userSpaceOnUse"
              x={offsetX}
              y={offsetY}
            >
              <circle cx={gridSize / 2} cy={gridSize / 2} r={1} fill="white" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {!loaded && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ opacity: 0.3, fontSize: 13 }}>Syncing…</span>
          </div>
        )}

        {loaded && (
          <>
            <div
              className="canvas-layer"
              style={{
                transform: `translate(${camera.x}px, ${camera.y}px) scale(${camera.zoom})`,
              }}
            >
              {items.map((item) => {
                const selected = selectedId === item.id;
                const editing = editingTextId === item.id && isTextItem(item);
                return (
                  <div
                    key={item.id}
                    className={`canvas-item ${isTextItem(item) ? "canvas-text-item" : ""} ${selected ? "selected" : ""}`}
                    style={{
                      left: item.x,
                      top: item.y,
                      width: item.width,
                      height: item.height,
                    }}
                    onMouseDown={(e) => onItemMouseDown(e, item.id)}
                    onContextMenu={(e) => onContextMenu(e, item.id)}
                    onDoubleClick={() => {
                      if (isTextItem(item)) startEditingText(item.id, false);
                    }}
                  >
                    {isImageItem(item) ? (
                      <img
                        src={item.src}
                        alt=""
                        draggable={false}
                        onError={(e) => {
                          const el = e.currentTarget;
                          const attempt = Number(el.dataset.retry || 0);
                          if (attempt < 3) {
                            el.dataset.retry = String(attempt + 1);
                            setTimeout(() => {
                              el.src = item.src + (item.src.includes("?") ? "&" : "?") + "r=" + attempt;
                            }, 1000 * (attempt + 1));
                          }
                        }}
                      />
                    ) : editing ? (
                      <textarea
                        ref={textareaRef}
                        className="canvas-text-editor"
                        value={editingDraft}
                        onChange={(e) => setEditingDraft(e.target.value)}
                        onBlur={commitTextEdit}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            e.preventDefault();
                            cancelTextEdit();
                          }
                          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                            e.preventDefault();
                            commitTextEdit();
                          }
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                          fontSize: item.fontSize,
                          lineHeight: String(item.lineHeight),
                        }}
                      />
                    ) : (
                      <div
                        className="canvas-text-content"
                        style={{
                          fontSize: item.fontSize,
                          lineHeight: String(item.lineHeight),
                        }}
                      >
                        {item.text}
                      </div>
                    )}
                    {!editing && (
                      <div
                        className="resize-handle"
                        onMouseDown={(e) => onResizeMouseDown(e, item.id)}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {showHint && (
              <div className={`hint${isDemo ? " hint-demo" : ""}`}>
                Paste an image, drag files in, or use Text to place copy on the canvas
              </div>
            )}
          </>
        )}

        <div className="toolbar">
          <button
            className={activeTool === "text" ? "is-active" : ""}
            onClick={() => setActiveTool((tool) => (tool === "text" ? "pan" : "text"))}
          >
            Text
          </button>
          <div className="separator" />
          <button onClick={() => setCamera((c) => ({ ...c, zoom: Math.min(5, c.zoom * 1.2) }))}>
            +
          </button>
          <span className="zoom-display">{Math.round(camera.zoom * 100)}%</span>
          <button onClick={() => setCamera((c) => ({ ...c, zoom: Math.max(0.1, c.zoom / 1.2) }))}>
            -
          </button>
          <div className="separator" />
          <button onClick={fitToScreen}>Fit</button>
          <button onClick={() => setCamera({ x: 0, y: 0, zoom: 1 })}>Reset</button>
        </div>

        {contextMenu && (
          <div
            className="context-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            {contextMenu.itemId ? (
              <>
                <button
                  onClick={() => {
                    bringToFront(contextMenu.itemId!);
                    setContextMenu(null);
                  }}
                >
                  Bring to front
                </button>
                <button
                  onClick={() => {
                    sendToBack(contextMenu.itemId!);
                    setContextMenu(null);
                  }}
                >
                  Send to back
                </button>
                <button
                  onClick={() => {
                    void duplicateItem(contextMenu.itemId!);
                    setContextMenu(null);
                  }}
                >
                  Duplicate
                </button>
                {items.find((item) => item.id === contextMenu.itemId)?.type === "text" && (
                  <button
                    onClick={() => {
                      startEditingText(contextMenu.itemId!, false);
                      setContextMenu(null);
                    }}
                  >
                    Edit text
                  </button>
                )}
                <button
                  className="danger"
                  onClick={() => {
                    deleteItem(contextMenu.itemId!);
                    setContextMenu(null);
                  }}
                >
                  Delete
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    fitToScreen();
                    setContextMenu(null);
                  }}
                >
                  Fit to screen
                </button>
                <button
                  onClick={() => {
                    setCamera({ x: 0, y: 0, zoom: 1 });
                    setContextMenu(null);
                  }}
                >
                  Reset view
                </button>
                <button
                  onClick={() => {
                    setActiveTool("text");
                    setContextMenu(null);
                  }}
                >
                  Text tool
                </button>
                {items.length > 0 && (
                  <button
                    className="danger"
                    onClick={() => {
                      clearAll();
                      setContextMenu(null);
                    }}
                  >
                    Clear all
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default App;
